import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, Star, Search, Archive, ArchiveRestore, Eye } from "lucide-react";
import TopBar from "@/components/TopBar";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CATEGORIES, categoryLabel, categoryMeta } from "@/lib/categories";
import { ConfirmModal } from "@/components/AppDialogs";

const ALL_TABS = [
  { slug: "all", label: { fr: "Toutes", en: "All" } },
  ...CATEGORIES.map((c) => ({ slug: c.slug, ...c })),
  { slug: "special", label: { fr: "Spéciaux", en: "Special" }, color: "#6B2C8F" },
  { slug: "archived", label: { fr: "Archivés", en: "Archived" }, color: "#5C4664" },
];

export default function AdminCatalog() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Catalog mutations (create, edit, archive, delete) are reserved to ops and
  // super_admin/admin. Commercial sees the catalog in read-only mode (per the
  // product spec — special-gift creation goes through the last-minute drawer
  // on the event detail page, not this global catalog).
  const canEdit = user?.role === "operations" || user?.role === "super_admin" || user?.role === "admin";
  const [products, setProducts] = useState([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("all");

  const load = async () => {
    setLoading(true);
    // Fetch active + archived counts in parallel.
    const [active, archived] = await Promise.all([
      api.get(activeCat === "archived"
        ? "/admin/products?include_archived=only"
        : "/admin/products"),
      activeCat === "archived"
        ? Promise.resolve({ data: [] })
        : api.get("/admin/products?include_archived=only"),
    ]);
    setProducts(active.data);
    if (activeCat !== "archived") {
      setArchivedCount(archived.data.length);
    } else {
      setArchivedCount(active.data.length);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [activeCat]);

  const [deleteConfirm, setDeleteConfirm] = useState(null); // product
  const [archiveConfirm, setArchiveConfirm] = useState(null); // product

  const remove = (p) => setDeleteConfirm(p);
  const performDelete = async () => {
    const p = deleteConfirm;
    setDeleteConfirm(null);
    if (!p) return;
    await api.delete(`/admin/products/${p.id}`);
    load();
  };

  const toggleArchive = (p) => setArchiveConfirm(p);
  const performArchive = async () => {
    const p = archiveConfirm;
    setArchiveConfirm(null);
    if (!p) return;
    await api.patch(`/admin/products/${p.id}`, { archived: !p.archived });
    load();
  };

  // Filter by search + category. "special" tab returns is_special. "archived" tab
  // is already filtered server-side so we just keep all results.
  const filtered = useMemo(() => products.filter((p) => {
    if (search && !`${p.name} ${p.description}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (activeCat === "all" || activeCat === "archived") return true;
    if (activeCat === "special") return p.is_special;
    return p.category === activeCat;
  }), [products, search, activeCat]);

  // Group filtered products by category for the "all" view, otherwise single bucket.
  const groups = useMemo(() => {
    if (activeCat === "archived") {
      return [{ slug: "archived", label: lang === "fr" ? "Cadeaux archivés" : "Archived gifts", items: filtered }];
    }
    if (activeCat !== "all") {
      const label = activeCat === "special"
        ? categoryLabel("special", lang)
        : categoryLabel(activeCat, lang);
      return [{ slug: activeCat, label, items: filtered }];
    }
    // Standard categories first, then special
    const out = [];
    CATEGORIES.forEach((c) => {
      const items = filtered.filter((p) => p.category === c.slug && !p.is_special);
      if (items.length) out.push({ slug: c.slug, label: categoryLabel(c.slug, lang), items });
    });
    const specials = filtered.filter((p) => p.is_special);
    if (specials.length) out.push({ slug: "special", label: categoryLabel("special", lang), items: specials });
    return out;
  }, [filtered, activeCat, lang]);

  // Counts for chips badges
  const counts = useMemo(() => {
    const c = { all: products.length, special: 0, archived: archivedCount };
    CATEGORIES.forEach((cat) => { c[cat.slug] = 0; });
    products.forEach((p) => {
      if (p.is_special) c.special += 1;
      if (c[p.category] != null) c[p.category] += 1;
    });
    return c;
  }, [products, archivedCount]);

  return (
    <div className="min-h-screen bg-[#FFF9FB]">
      <TopBar />
      <div className="max-w-6xl mx-auto px-5 pb-24">
        <Button
          variant="ghost"
          onClick={() => navigate("/admin")}
          data-testid="catalog-back"
          className="gap-2 -ml-3 mt-2 text-[#5C4664] hover:bg-[#FCE7F3]"
        >
          <ArrowLeft className="w-4 h-4" /> {t("admin.backToDashboard")}
        </Button>

        <div className="mt-6 flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D81B8C] mb-2">
              {t("common.appName")}
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C]">
              {t("admin.catalogPage.title")}
            </h1>
            <p className="mt-2 text-sm text-[#5C4664] max-w-2xl">
              {canEdit
                ? t("admin.catalogPage.sub")
                : "Vue lecture seule — réservée aux opérations & administrateurs pour la modification."}
            </p>
            {!canEdit && (
              <div data-testid="catalog-readonly-banner" className="mt-3 inline-flex items-center gap-1.5 bg-[#F2E2EC] text-[#5C4664] text-xs font-semibold px-3 py-1.5 rounded-full">
                <Eye className="w-3.5 h-3.5" />
                Lecture seule
              </div>
            )}
          </div>
          {canEdit && (
            <Button
              onClick={() => navigate("/admin/catalog/new")}
              data-testid="new-product-btn"
              className="rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] gap-2"
            >
              <Plus className="w-4 h-4" /> {t("admin.catalogPage.newProduct")}
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="mt-7 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C4664]" />
          <Input
            data-testid="catalog-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher dans le catalogue…"
            className="pl-9 h-11 rounded-xl border-[#F2E2EC]"
          />
        </div>

        {/* Category chips */}
        <div className="mt-5 -mx-5 px-5 overflow-x-auto pb-1">
          <div className="flex gap-2 min-w-max">
            {ALL_TABS.map((tab) => {
              const active = activeCat === tab.slug;
              const Icon = tab.icon;
              const meta = tab.slug !== "all" && tab.slug !== "special" && tab.slug !== "archived"
                ? categoryMeta(tab.slug)
                : null;
              const color = tab.color || meta?.color || "#D81B8C";
              const label = typeof tab.label === "object" ? tab.label[lang] : tab.label;
              return (
                <button
                  key={tab.slug}
                  onClick={() => setActiveCat(tab.slug)}
                  data-testid={`catalog-cat-${tab.slug}`}
                  className={`flex items-center gap-2 px-4 h-10 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                    active
                      ? "bg-[#2A0E3C] text-white"
                      : "bg-white text-[#2A0E3C] border border-[#F2E2EC] hover:border-[#D81B8C]/40"
                  }`}
                >
                  {Icon ? <Icon className="w-4 h-4" style={{ color: active ? "#fff" : color }} /> : null}
                  {tab.slug === "special" && !Icon ? <Star className="w-4 h-4" style={{ color: active ? "#fff" : color }} /> : null}
                  {tab.slug === "archived" && !Icon ? <Archive className="w-4 h-4" style={{ color: active ? "#fff" : color }} /> : null}
                  {label}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    active ? "bg-white/20 text-white" : "bg-[#FCE7F3] text-[#D81B8C]"
                  }`}>{counts[tab.slug] ?? 0}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="py-16 text-center text-[#5C4664]">{t("common.loading")}</div>
          ) : groups.length === 0 || groups.every((g) => g.items.length === 0) ? (
            <div data-testid="catalog-empty" className="py-16 text-center text-[#5C4664]">
              {t("admin.catalogPage.noProducts")}
            </div>
          ) : (
            <div className="space-y-12">
              {groups.map((group) => {
                const meta = group.slug !== "special" ? categoryMeta(group.slug) : null;
                const color = group.slug === "special" ? "#6B2C8F" : meta?.color || "#D81B8C";
                const Icon = group.slug === "special" ? Star : meta?.icon;
                return (
                  <section key={group.slug} data-testid={`catalog-section-${group.slug}`}>
                    <div className="flex items-center gap-3 mb-5">
                      {Icon && (
                        <div
                          className="w-9 h-9 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${color}15`, color }}
                        >
                          <Icon className="w-4 h-4" />
                        </div>
                      )}
                      <h2 className="font-display text-xl sm:text-2xl font-black text-[#2A0E3C]">
                        {group.label}
                      </h2>
                      <span className="text-xs font-semibold text-[#5C4664]">
                        · {group.items.length} produit(s)
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {group.items.map((p) => (
                        <div
                          key={p.id}
                          data-testid={`admin-product-${p.id}`}
                          data-archived={p.archived ? "true" : "false"}
                          className={`bg-white rounded-2xl border overflow-hidden hover:border-[#D81B8C]/40 transition-colors ${
                            p.archived ? "border-[#5C4664]/30 opacity-80" : "border-[#F2E2EC]"
                          }`}
                        >
                          <div className="aspect-square w-full bg-[#FCE7F3] overflow-hidden relative">
                            {p.image_url ? (
                              <img src={p.image_url} alt={p.name} className={`w-full h-full object-cover ${p.archived ? "grayscale" : ""}`} />
                            ) : null}
                            {p.archived && (
                              <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-black/70 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-full">
                                <Archive className="w-3 h-3" /> Archivé
                              </span>
                            )}
                          </div>
                          <div className="p-3">
                            <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-[#5C4664]">
                              {p.is_special && <Star className="w-3 h-3 text-[#6B2C8F]" />}
                              {categoryLabel(p.category, lang)}
                            </div>
                            <h3 className="mt-1 font-display text-xs sm:text-sm font-black text-[#2A0E3C] leading-snug line-clamp-2">
                              {p.name}
                            </h3>
                            <div className="mt-1.5 font-display text-base font-black text-[#D81B8C]">
                              ${p.price.toFixed(0)}
                            </div>
                            {canEdit && (
                              <div className="mt-2.5 flex gap-1.5">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(`/admin/catalog/${p.id}/edit`)}
                                  data-testid={`edit-product-${p.id}`}
                                  className="rounded-full border-[#F2E2EC] flex-1 h-8 px-2"
                                  title="Modifier"
                                >
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => toggleArchive(p)}
                                  data-testid={`archive-product-${p.id}`}
                                  className="rounded-full border-[#F2E2EC] h-8 px-2"
                                  title={p.archived ? "Rétablir" : "Archiver"}
                                >
                                  {p.archived
                                    ? <ArchiveRestore className="w-3 h-3 text-[#3FA535]" />
                                    : <Archive className="w-3 h-3 text-[#5C4664]" />}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => remove(p)}
                                  data-testid={`delete-product-${p.id}`}
                                  className="rounded-full border-[#F2E2EC] text-[#D81B8C] hover:bg-[#FCE7F3] hover:text-[#D81B8C] h-8 px-2"
                                  title="Supprimer définitivement"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={performDelete}
        title="Supprimer ce produit ?"
        description={deleteConfirm ? `« ${deleteConfirm.name} » sera définitivement supprimé du catalogue.` : ""}
        confirmLabel="Supprimer"
        destructive
        testId="catalog-delete-confirm"
      />
      <ConfirmModal
        open={!!archiveConfirm}
        onClose={() => setArchiveConfirm(null)}
        onConfirm={performArchive}
        title={archiveConfirm?.archived ? "Rétablir ce produit ?" : "Archiver ce produit ?"}
        description={
          archiveConfirm
            ? (archiveConfirm.archived
                ? `« ${archiveConfirm.name} » sera rétabli au catalogue actif.`
                : `« ${archiveConfirm.name} » ne sera plus visible dans la boutique invité.`)
            : ""
        }
        confirmLabel={archiveConfirm?.archived ? "Rétablir" : "Archiver"}
        testId="catalog-archive-confirm"
      />
    </div>
  );
}
